import type { FieldObstacle, FieldPoint, FieldSpec } from './field';
import type { DirectedLapLine } from './laps';
import type { TrackPoint } from './types';

// L3 확정 도면: 2600×1800u. 마블 r18u를 엔진 r10px에 맞춘다.
export const RING_CIRCUIT_SCALE = 10 / 18;
const S = RING_CIRCUIT_SCALE;
const p = (x: number, y: number): FieldPoint => ({ x: x * S, y: y * S });
const rect = (x: number, y: number, w: number, h: number) => ({ ...p(x, y), w: w * S, h: h * S });
const vertical = (x: number, y1: number, y2: number, direction: number): DirectedLapLine => ({ a: p(x, y1), b: p(x, y2), forward: { x: direction, y: 0 } });
const horizontal = (y: number, x1: number, x2: number, direction: number): DirectedLapLine => ({ a: p(x1, y), b: p(x2, y), forward: { x: 0, y: direction } });

function circuitLoop(): FieldPoint[] {
  const points = [p(720, 1400)];
  const arc = (cx: number, cy: number, from: number) => {
    for (let i = 0; i <= 18; i += 1) {
      const angle = (from + i * 90 / 18) * Math.PI / 180;
      const point = p(cx + Math.cos(angle) * 380, cy + Math.sin(angle) * 380);
      if (Math.hypot(point.x - points.at(-1)!.x, point.y - points.at(-1)!.y) > .01) points.push(point);
    }
  };
  // 시계 방향. 랩선은 좌하단 커브에 가까우므로 마지막 직선의 끝과 연결한다.
  arc(720, 1020, 90);
  arc(720, 660, 180);
  arc(1860, 660, 270);
  arc(1860, 1020, 0);
  points.push(p(720, 1400));
  return points;
}

const loop = circuitLoop();
const cut = [p(1290, 280), p(1290, 590), p(1290, 840), p(1290, 1110), p(1290, 1400)];
const finishPath = [p(1120, 1400), p(875, 1400), p(755, 1480), p(730, 1610), p(730, 1720)];
const lapReturnLength = Math.hypot(294, 318);
// Keep the rotating blades, but leave a marble-width passing gap even when they
// are perpendicular to the straight. Taper back before either lap checkpoint.
const paddleBays = [940, 1660].map((x) => ({
  kind: 'poly' as const,
  points: [[x - 180, 195], [x - 60, 155], [x + 60, 155], [x + 180, 195],
    [x + 180, 365], [x + 60, 405], [x - 60, 405], [x - 180, 365]].map(([px, py]) => p(px, py)),
}));
const waveGates = [1500, 1390].flatMap((x, index) => [-1, 1].map((side): FieldObstacle => ({
  kind: 'gate', id: `rc-wave-${index}-${side}`,
  ...rect(x, side < 0 ? 1312 : 1424, 11, 64),
  schedule: { type: 'cycle', period: 2.4, openSeconds: 1.3, phase: index * .65 + (side > 0 ? 1.2 : 0) },
})));

export const RING_CIRCUIT_FIELD: FieldSpec = {
  shapes: [
    { kind: 'pipe', points: loop, width: 170 * S },
    ...paddleBays,
    // Increased upstream throughput also needs room around the alternating pins.
    // Keep the pins and transverse pumps, but remove the single-file wall slots.
    { kind: 'poly', points: [[1500, 1315], [1580, 1280], [2030, 1280], [2130, 1315], [2130, 1485], [2030, 1520], [1580, 1520], [1500, 1485]].map(([x, y]) => p(x, y)) },
    { kind: 'ellipse', ...p(300, 840), rx: 232 * S, ry: 300 * S },
    { kind: 'pipe', points: cut, width: 130 * S },
    { kind: 'disc', ...p(1290, 840), r: 232 * S },
    // The mouth catches momentum beyond the turn and funnels into a four-marble
    // exit without flat shoulders where lap-one and finishing traffic can pile up.
    { kind: 'poly', points: [[600, 1390], [680, 1190], [900, 1190], [1030, 1315], [1030, 1485], [820, 1620], [640, 1620]].map(([x, y]) => p(x, y)) },
    { kind: 'pipe', points: [p(730, 1580), p(730, 1730)], width: 180 * S },
  ],
  holes: [],
  gravity: { x: 0, y: 0 },
  damping: .4,
  wallBounce: true,
  spawn: { kind: 'rect', ...rect(84, 590, 432, 500), pitch: 40 * S, fill: 'center' },
  startGate: rect(175, 566, 300, 14),
  extraStartGates: [rect(175, 1100, 300, 14)],
  finish: { kind: 'rect', ...rect(635, 1690, 190, 75) },
  circuit: {
    laps: 2, loop,
    checkpoints: [vertical(1120, 190, 370, 1), vertical(1860, 190, 370, 1), horizontal(840, 2150, 2330, 1), vertical(1700, 1270, 1530, -1)],
    lapLine: vertical(720, 1180, 1490, -1),
    // Count the exit before the pipe joins the wide loop (its top edge is y=1315).
    // At y=1360 a fast ball could already turn sideways and miss the finite line.
    shortcut: { path: cut, afterCheckpoint: 1, fromLap: 2, checkpoints: [horizontal(380, 1220, 1360, 1), horizontal(840, 1045, 1535, 1), horizontal(1300, 1215, 1365, 1)], intake: rect(1215, 245, 150, 160), intakeAcceleration: 400 * S },
    finishPath, finishApproach: .91, driveAcceleration: 180 * S,
  },
  obstacles: [
    { kind: 'gate', id: 'rc-cut-entry', ...rect(1215, 357, 150, 12), lapRule: 'shortcut-entry', schedule: { type: 'cycle', period: 2.8, openSeconds: 1.1 }, eventType: 'circuit-cut-open' },
    { kind: 'gate', id: 'rc-cut-exit', ...rect(1215, 1320, 150, 12), lapRule: 'shortcut-exit', schedule: { type: 'open-at', at: Infinity } },
    // An angled deflector turns eligible finishers downward instead of stopping
    // them head-on against lap-one traffic. Lap one still passes through it.
    { kind: 'gate', id: 'rc-lap-return', ...rect(833 - lapReturnLength / 2, 1330, lapReturnLength, 14), angle: Math.atan2(318, -294), lapRule: 'lap-return', schedule: { type: 'open-at', at: Infinity } },
    { kind: 'gate', id: 'rc-finish-entry', ...rect(593, 1476, 420, 12), lapRule: 'finish-entry', schedule: { type: 'open-at', at: Infinity } },
    ...[[940, 280, 24, 1], [1660, 280, -22, -1]].map(([x, y, phase, direction], index): FieldObstacle => ({
      kind: 'paddle', id: `rc-paddle-${index}`, ...p(x, y), length: 120 * S, thickness: 11 * S,
      speed: direction * 110 * Math.PI / 180, phase: phase * Math.PI / 180,
    })),
    // Break pressure arches between the two lap-filtered streams with a real
    // moving contact surface. No marble-specific release or invisible impulse.
    { kind: 'paddle', id: 'rc-junction-sweeper', ...p(835, 1420), length: 140 * S, thickness: 11 * S, speed: Math.PI * .65, phase: Math.PI / 5, reverseEvery: 2.6 },
    ...[[1204, 740], [1382, 756], [1180, 912], [1392, 922], [1290, 840], [1290, 672]].map(([x, y], i): FieldObstacle => ({ kind: 'bumper', id: `rc-bumper-${i}`, ...p(x, y), r: 28 * S, restitution: .7 })),
    // 핀-벽 쐐기에 공이 끼지 않도록 벽 여유 43u 이상, 핀끼리 55u 이상을 남긴다.
    ...[[2000, 1380], [1920, 1420], [1840, 1380], [1760, 1420], [1680, 1380], [1600, 1420]].map(([x, y], i): FieldObstacle => ({ kind: 'pin', id: `rc-peg-${i}`, ...p(x, y), r: 17 * S, sink: i % 2 === 0 })),
    ...waveGates,
  ],
  zones: [
    ...[840, 1030, 1560, 1750].map((x) => ({ kind: 'accel' as const, rect: rect(x - 45, 196, 90, 168), ax: 95 * S, ay: 0, eventType: 'boost-surge' as const, eventScale: 1.8 })),
    { kind: 'pump', rect: rect(1625, 1280, 410, 240), ax: 0, ay: 80 * S, period: 1.4 },
    { kind: 'pump', rect: rect(960, 1315, 96, 62), ax: 0, ay: 75 * S, period: 1.2 },
    { kind: 'pump', rect: rect(1025, 1423, 96, 62), ax: 0, ay: -75 * S, period: 1.2, phase: .6 },
  ],
  events: [
    { type: 'boost-surge', duration: 4, window: [14, 22], trigger: { maxProgress: .69 }, required: true },
    { type: 'circuit-cut-open', duration: 4, window: [4, 12], trigger: { lap: 2, maxProgress: .76 }, required: true },
    { type: 'circuit-pin-sink', duration: 5, window: [44, 50], trigger: { maxProgress: .91, fireByProgress: .81 } },
  ],
  eventZones: [
    { type: 'boost-surge', rect: rect(800, 190, 1020, 180) },
    { type: 'circuit-cut-open', rect: rect(1180, 210, 220, 390), minLap: 2 },
    { type: 'circuit-pin-sink', rect: rect(1560, 1270, 500, 260) },
  ],
  landmarks: [
    { ...p(300, 570), label: '출발 베이 · 2랩 레이스' },
    { ...p(950, 165), label: '부스터 직선' },
    { ...p(1290, 520), label: '2랩 전용 · 인필드 지름길' },
    { ...p(1290, 1080), label: '범퍼 아레나' },
    { ...p(1900, 1550), label: '페그 셔플' },
    { ...p(1400, 1550), label: '엇박자 파도 게이트' },
    { ...p(520, 1280), label: '1랩 ←   2랩 결승 ↓' },
    { ...p(1050, 1620), label: '합류 스위퍼' },
  ],
  stallSeconds: 3.4,
  rushAt: 120,
  maxSpeed: 700 * S,
  velocityWatchdog: { minSpeed: 8 * S, seconds: 1.2, impulse: 130 * S },
  // 실제 장치 접근과 2랩 자격을 모두 확인한다. 물리 시간 그대로 재생한다.
  eventMinGapSeconds: 9,
  eventFinalGuardSeconds: 6,
  eventScheduling: 'occupancy',
};

export function ringCircuitGuidePath(): TrackPoint[] {
  const firstStart = loop.findIndex((point) => point.y < 840 * S && point.x < 400 * S);
  return [p(300, 840), ...loop.slice(firstStart), ...loop.slice(1), ...finishPath].map((point) => ({ ...point, width: 170 * S }));
}
